import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Virtuoso } from "react-virtuoso";

import EmptyState from "ds/components/EmptyState";
import { SearchQueryOrderDirection, Space } from "types/generated";
import {
  convertToTreeStructure,
  isNodeVisible,
  search,
  TreeNode,
  updateTreeRendering,
} from "utils/tree";
import { Space as SpaceIcon } from "components/icons/generated";
import Box from "ds/components/Box";

import SpacesTreeGridListSortHeader from "./SortHeader";
import { FilterItemKeys } from "../constants";
import styles from "./styles.module.css";
import SpacesTreeGridListRow from "./Row";
import { getSortPredicate } from "../utils";
import { SpaceWithParentAccessLevel } from "../../types";
import { createSpacesWithParentAccessLevel } from "../../utils";

type SpacesTreeGridListProps = {
  spaces: Space[];
  sortBy: FilterItemKeys;
  sortDirection: SearchQueryOrderDirection;
  onSortChange: (option: FilterItemKeys) => void;
  isRootAdmin: boolean;
  activeId?: string;
  searchQuery?: string;
  canNonRootAdminCreateSpace: boolean;
};

const SpacesTreeGridList = ({
  spaces,
  isRootAdmin,
  sortBy,
  sortDirection,
  onSortChange,
  activeId,
  searchQuery,
  canNonRootAdminCreateSpace,
}: SpacesTreeGridListProps) => {
  const prevSearchQueryRef = useRef(searchQuery);

  const spacesWithParentAccess: Array<SpaceWithParentAccessLevel> = useMemo(
    () => createSpacesWithParentAccessLevel(spaces),
    [spaces]
  );

  const { flatList } = useMemo(
    () =>
      convertToTreeStructure<SpaceWithParentAccessLevel>(
        spacesWithParentAccess,
        "id",
        "parentSpace",
        "name",
        getSortPredicate(sortBy, sortDirection)
      ),
    [sortBy, sortDirection, spacesWithParentAccess]
  );

  const allKeysSet = useMemo(() => {
    const allKeys = flatList.map((item) => item.id);
    return new Set(allKeys);
  }, [flatList]);

  const [expandedKeys, setExpandedKeys] = useState(allKeysSet);
  const [renderedKeys, setRenderedKeys] = useState(allKeysSet);

  const visibleNodes = useMemo(
    () => flatList.filter((node) => isNodeVisible(node, renderedKeys, expandedKeys)),
    [expandedKeys, renderedKeys, flatList]
  );

  const activeItemIndex = useMemo(() => {
    if (!activeId) return 0;
    return visibleNodes.findIndex((node) => node.id === activeId);
  }, [activeId, visibleNodes]);

  useEffect(() => {
    const queryChanged = searchQuery !== prevSearchQueryRef.current;
    prevSearchQueryRef.current = searchQuery;

    if (!searchQuery) {
      setRenderedKeys(allKeysSet);

      if (queryChanged) {
        setExpandedKeys(allKeysSet);
      }

      return;
    }

    const matchedPaths = search(flatList, searchQuery, ["name", "id"]);

    const { expandedNodes, renderedNodes } = updateTreeRendering(flatList, matchedPaths);

    setRenderedKeys(renderedNodes);

    if (queryChanged) {
      setExpandedKeys(expandedNodes);
    }
  }, [allKeysSet, flatList, searchQuery]);

  const toggleKey = useCallback(
    (key: string) => () => {
      setExpandedKeys((prev) => {
        const newKeys = new Set(prev);
        if (newKeys.has(key)) {
          newKeys.delete(key);
        } else {
          newKeys.add(key);
        }
        return newKeys;
      });
    },
    [setExpandedKeys]
  );

  const itemContent = useCallback(
    (_: number, node: TreeNode<SpaceWithParentAccessLevel>) => {
      const isActive = activeId === node.id;

      return (
        <SpacesTreeGridListRow
          key={node.id}
          node={node}
          isActive={isActive}
          isExpanded={expandedKeys.has(node.id)}
          isRootAdmin={isRootAdmin}
          onToggle={toggleKey(node.id)}
          searchQuery={searchQuery}
          canNonRootAdminCreateSpace={canNonRootAdminCreateSpace}
        />
      );
    },
    [activeId, expandedKeys, isRootAdmin, toggleKey, searchQuery, canNonRootAdminCreateSpace]
  );

  return (
    <Box direction="column" grow="1" role="treegrid" aria-label="Spaces">
      {visibleNodes.length > 0 && (
        <SpacesTreeGridListSortHeader
          sortOption={sortBy}
          sortDirection={sortDirection}
          onSortChange={onSortChange}
        />
      )}

      <Box direction="column" grow="1" className={styles.listWrapper} role="rowgroup" fullWidth>
        {visibleNodes.length > 0 ? (
          <Virtuoso
            data={visibleNodes}
            itemContent={itemContent}
            initialTopMostItemIndex={activeItemIndex}
            overscan={300}
          />
        ) : (
          <EmptyState
            title="No results"
            icon={SpaceIcon}
            caption="Try different search."
            announce
          />
        )}
      </Box>
    </Box>
  );
};

export default SpacesTreeGridList;
